import React, { useState, useRef, useEffect } from 'react'
import { propType } from 'graphql-anywhere'
import PropTypes from 'prop-types'
import styles from './player-body-rate-options.css'
import {
  UserRatingScale,
  getRatingValueToUpdate,
  shouldDisplayPopover
} from './shared'
import RateOption from './rate-option'
import {
  useUserRatingQuery,
  useUserRatingMutation
} from './user-rating-query-mutation'
import CONTENT_ITEM_LIGHT_FRAGMENT from '../../../graphql/fragments/content-item-light.gql'

const popoverDurationInMs = 2500 // hide popover after 2.5s

// contentItem is episode or movie
const PlayerBodyRateOptions = React.memo(
  ({ contentItem, setRateOptionClicked }) => {
    if (!contentItem) return null
    const seriesOrMovieId =
      contentItem.__typename === 'Episode'
        ? contentItem.series.id
        : contentItem.id

    const [userRating, setRating] = useState(null)
    const [isPopoverOpen, setPopoverOpen] = useState(false)
    const [popeverText, setPopeverText] = useState('')
    const timeoutRef = useRef(null) // store timer for hiding the popover
    const [popoverArrowPosition, setPopoverArrowPosition] = useState(0)
    const notMyThingRef = useRef(null)
    const solidWatchRef = useRef(null)
    const topPickRef = useRef(null)

    // in onRateOptionClicked, we display the popover and hide it after 2.5s
    const hidePopover = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        setPopoverOpen(false)
        timeoutRef.current = null
      }, popoverDurationInMs)
    }
    // clear timer when unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    }, [])

    // get mutation to be called later in onRateOptionClicked
    const { userRatingMutation } = useUserRatingMutation({
      skip: false,
      setRating
    })

    // when RateOption clicked, call `userRatingMutation`, and update state using `userRatingMutationData` after mutation is successful
    const onRateOptionClicked = (ratingValueClicked, rateOptionRef) => {
      setRateOptionClicked(true)
      const ratingValueToUpdate = getRatingValueToUpdate(
        userRating,
        ratingValueClicked
      )
      userRatingMutation({
        variables: {
          input: {
            contentItemId: seriesOrMovieId,
            userRating: ratingValueToUpdate
          }
        }
      })
      const { shouldDipslayPopover, popoverCopy } = shouldDisplayPopover(
        ratingValueToUpdate
      )
      if (shouldDipslayPopover) {
        setPopoverOpen(true)
        setPopeverText(popoverCopy)
        // Get position of RateOption and update position of popoverArrow
        if (rateOptionRef && rateOptionRef.current) {
          const rect = rateOptionRef.current.getBoundingClientRect()
          setPopoverArrowPosition(rect.left + 6) // adjust based on size of `rateIcon` in `rate-option.css`
        }

        hidePopover()
      } else {
        setPopoverOpen(false)
      }
    }

    // fetch user rating
    useUserRatingQuery({
      contentItemId: seriesOrMovieId,
      setRating
    })

    return (
      <div className={styles.rateOptionsWrapper}>
        <div className={styles.rateOptionsRow}>
          <RateOption
            ref={notMyThingRef}
            defaultScale={UserRatingScale.NOT_MY_THING}
            userRating={userRating}
            onRateClick={onRateOptionClicked}
            inPlayer
          />
          <RateOption
            ref={solidWatchRef}
            defaultScale={UserRatingScale.SOLID_WATCH}
            userRating={userRating}
            onRateClick={onRateOptionClicked}
            inPlayer
          />
          <RateOption
            ref={topPickRef}
            defaultScale={UserRatingScale.TOP_PICK}
            userRating={userRating}
            onRateClick={onRateOptionClicked}
            inPlayer
          />
        </div>
        <div
          className={styles.popover}
          style={{ visibility: isPopoverOpen ? 'visible' : 'hidden' }}
        >
          {popeverText}
          <div
            className={styles.popoverArrow}
            style={{ left: popoverArrowPosition }}
          />
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    const shouldSkipRender =
      prevProps.contentItem.id === nextProps.contentItem.id &&
      prevProps.setRateOptionClicked === nextProps.setRateOptionClicked
    return shouldSkipRender
  }
)

PlayerBodyRateOptions.propTypes = {
  setRateOptionClicked: PropTypes.func.isRequired,
  contentItem: propType(CONTENT_ITEM_LIGHT_FRAGMENT)
}

export default PlayerBodyRateOptions
